import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { DateTime } from "luxon";
import { useQuery } from "@tanstack/react-query";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, useMap } from "react-leaflet";
import { divIcon } from "leaflet";
import type { LatLngBoundsExpression, LatLngTuple } from "leaflet";
import "leaflet/dist/leaflet.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faLocationDot } from "@fortawesome/free-solid-svg-icons";

import { telemetryAjax } from "../../ajax/TelemetryAjax";
import { stoppagesAjax } from "../../ajax/StoppagesAjax";
import { OptionResponse } from "../../DTOs/Common/OptionResponse";
import GeofenceLayer from "./GeofenceLayer";
import HaulRoadLayer from "./HaulRoadLayer";
import BlastPatternFenceLayer from "./BlastPatternFenceLayer";
import DigBlockFenceLayer from "./DigBlockFenceLayer";
import MapFeatureLayer from "./MapFeatureLayer";
import { LayerStack, StackedGroup } from "./LayerStack";
import { LAYER_RANK } from "./layerStackOrder";
import { fenceCorners, useGeofences } from "./useGeofences";
import { useMapFeatures } from "./useMapFeatures";
import { useMapZoom } from "./useMapZoom";
import { Basemap, readStoredBasemap, resolveBasemap, storeBasemap } from "./basemapPreference";
import { StoppageTypeDto } from "../../DTOs/Stoppage/StoppageTypeDto";
import { PlantPositionStatusDTO } from "../../DTOs/Telemetry/PlantPositionStatusDTO";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { Cancelled } from "../../types/ErrorTypes";
import { useSignalRContext } from "../Contexts/SignalRContext";
import { DashboardCardEmptyState } from "../Dashboard/DashboardCardEmptyState";
import {
  bearingBetween,
  equipmentLabel,
  equipmentMarkerHtml,
  isEquipmentType,
  unwrapHeading,
  useEquipmentSprite,
  OFFLINE_OPACITY,
  STATUS_COLOURS,
} from "./EquipmentMarker";

const MAX_MARKER_SIZE = 40;
const MIN_MARKER_SIZE = 20;
const FULL_SIZE_MARKER_ZOOM = 17;
const MARKER_SIZE_STEP_PER_ZOOM = 6;

function markerSize(zoom: number | null): number {
  if (zoom == null) return MAX_MARKER_SIZE;
  return Math.max(MIN_MARKER_SIZE, MAX_MARKER_SIZE - Math.max(0, FULL_SIZE_MARKER_ZOOM - zoom) * MARKER_SIZE_STEP_PER_ZOOM);
}

const MapZoomReporter: React.FC<{ onZoom: (zoom: number) => void }> = ({ onZoom }) => {
  const zoom = useMapZoom();
  useEffect(() => onZoom(zoom), [zoom, onZoom]);
  return null;
};

// Note: a ceiling has to exist - the site plan draws no tile layer, so Leaflet has no zoom limit of its
// own, and fitting the opening view to a single point (or to a map with no size yet) computes zoom
// Infinity, which nothing can zoom back out of. Above the OSM tiles' own 19 because a vector map has no
// tile pyramid to run out of; the tile layer scales its last native level past that.
const MAP_MAX_ZOOM = 22;
const OSM_MAX_NATIVE_ZOOM = 19;

// Note: snapped to whole levels, the opening view can only be the largest level the site fits in, which
// left up to half the box empty around it. Zero lets fitBounds settle on the fractional zoom that fills it.
const MAP_ZOOM_SNAP = 0;

// Note: how often the offline test is re-run. Nothing pushes an update for a machine that has gone
// quiet - silence is the signal - so this is what makes a machine fade rather than sitting there
// looking live until someone reloads. Well below the shortest offline threshold (120s).
const OFFLINE_RECHECK_MS = 15000;

// Note: pushes an offline marker underneath the live fleet. Transparency alone still leaves it
// competing for clicks and for the eye where machines overlap, and a stale position should never be
// what you hit when reaching for a working machine.
const OFFLINE_Z_OFFSET = -1000;

// Note: coarse on purpose. The exact number of seconds a machine has been quiet is noise; the useful
// distinction is minutes versus hours versus days.
// Note: seconds are still spelled out below a minute. Flooring to minutes produced "no report for
// 0 min", which reads as a contradiction of the word "offline" next to it - and it is reachable
// whenever the configured cadence cap is under two minutes.
function formatSilence(seconds: number): string {
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))} sec`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h`;
  return `${Math.floor(seconds / 86400)} d`;
}

// Note: above this, a machine with an open stoppage is contradicting its own record rather than just
// creeping on GPS noise. 5 kph is chosen off the noise floor measured on this fleet: the aftermarket
// receivers never report below 2.0 kph even when stationary, and standstills peak around 3.7 kph, so
// anything at 5 is really travelling.
const MOVING_WHILE_DOWN_KPH = 5;

const WORKING_STATUS = "up";
const OFFLINE_STATUS = "off";
const stoppageStatus = (stoppageTypeId: number) => `stoppage-${stoppageTypeId}`;

/**
 * Short codes for the key, so several states fit on one line. The full name is always one hover away,
 * and the popup never abbreviates.
 *
 * Note: keyed on StoppageTypeId rather than on the name, because the name is editable in
 * tblStoppageType and a rename should not silently drop a code back to a generated initialism.
 *
 * Note: type 5 is "Maintenance Down" in the database, hence MD. If the concept intended is
 * Unscheduled Maintenance then the fix is renaming the row in tblStoppageType - which flows through
 * here, the Stoppages screen and every report at once - rather than showing a code here that no other
 * screen uses.
 */
const STOPPAGE_TYPE_ABBREVIATIONS: Record<number, string> = {
  1: "SB",
  2: "PM",
  3: "OD",
  4: "NOD",
  5: "MD",
  6: "UC",
};

// Note: a generated initialism covers any type added to tblStoppageType after this was written, so a
// new type gets a usable code instead of breaking the key. Parenthetical qualifiers are dropped first -
// "Unclassified (Auto-Detected)" should not become "UA".
function abbreviateStoppageType(typeId: number | null, name: string | null): string {
  if (typeId != null && STOPPAGE_TYPE_ABBREVIATIONS[typeId])
    return STOPPAGE_TYPE_ABBREVIATIONS[typeId];

  if (!name)
    return "DOWN";

  const words = name.replace(/\(.*?\)/g, " ").trim().split(/\s+/).filter(Boolean);
  return words.length > 1
    ? words.map(w => w[0]).join("").toUpperCase().slice(0, 4)
    : name.slice(0, 2).toUpperCase();
}

function isOlder(candidate: PlantPositionStatusDTO, held: PlantPositionStatusDTO): boolean {
  return candidate.timestampUtc.toMillis() < held.timestampUtc.toMillis();
}

// Note: the snapshot decides which plants are on the map, but a live push already held for a plant wins
// over the snapshot's row when it is newer, because the snapshot can be read before that push was sent.
function mergeSnapshot(held: PlantPositionStatusDTO[], snapshot: PlantPositionStatusDTO[]): PlantPositionStatusDTO[] {
  const heldById = new Map(held.map(p => [p.plantId, p]));

  return snapshot.map(row => {
    const current = heldById.get(row.plantId);
    return current && isOlder(row, current) ? current : row;
  });
}

const MapResizeWatcher: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(map.getContainer());
    return () => observer.disconnect();
  }, [map]);

  return null;
};

type MachinePositionsMapViewProps = {
  title?: React.ReactNode;
  sitePlanOption?: boolean;
};

export const MachinePositionsMapView: React.FC<MachinePositionsMapViewProps> = ({ title, sitePlanOption = true }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { connection, isReconnecting } = useSignalRContext();

  const [plants, setPlants] = useState<PlantPositionStatusDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const spriteStatus = useEquipmentSprite();

  // Note: same query key and cache the Operations screen uses, so the two share one fetch and the key
  // can never show a colour the rest of Eclipse disagrees with. A failure yields an empty list, which
  // costs the stoppage rows of the key but leaves the map itself working.
  const { data: stoppageTypeOptions = new OptionResponse<StoppageTypeDto>([]) } = useQuery({
    queryKey: ["references", "stoppageTypes"],
    queryFn: async () => {
      const res = await stoppagesAjax.getStoppageTypes();
      if (ErrorResponse.isErrorResponse(res)) return new OptionResponse<StoppageTypeDto>([]);
      return res;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Note: sorted here rather than trusting the endpoint's order, which has no ORDER BY behind it.
  const stoppageTypes = useMemo(
    () => stoppageTypeOptions.groups
      .map(g => ({
        stoppageTypeId: g.key,
        stoppageType: g.label,
        colourHex: g.fullObject?.colourHex ?? null,
      }))
      .sort((a, b) => a.stoppageTypeId - b.stoppageTypeId),
    [stoppageTypeOptions]);

  // Note: a `use href` pointing into a sprite that never loaded draws nothing and reports nothing, so
  // the whole fleet would vanish from an otherwise healthy-looking map. Say so once, and fall back to
  // the plain dots below - a machine shown as a dot is worse than a silhouette, but a machine shown as
  // nothing is a lie about where the fleet is.
  useEffect(() => {
    if (spriteStatus !== "failed") return;

    enqueueSnackbar("Equipment icons could not be loaded - showing plain position markers.",
      { variant: "warning", autoHideDuration: 6000 });
  }, [spriteStatus, enqueueSnackbar]);

  // Note: the last bearing and position believed for each plant, so a machine that has stopped keeps
  // pointing the way it was last travelling. The feed sends headingDegrees only while moving, so
  // without this every parked machine would snap to north the moment it came to rest.
  const headingsRef = useRef<Map<number, { heading: number; known: boolean; latitude: number; longitude: number }>>(new Map());

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadSnapshot = useCallback(() => {
    telemetryAjax.getCurrentPositions()
      .then((result) => {
        if (!mountedRef.current) return;

        if (ErrorResponse.isErrorResponse(result)) {
          enqueueSnackbar(result.messages.map(m => m.description).join(", "), { variant: "error", autoHideDuration: 4000 });
          setLoadFailed(true);
          return;
        }

        setLoadFailed(false);
        setPlants(prev => mergeSnapshot(prev, result));
      })
      .catch((err) => {
        if (!mountedRef.current || err === Cancelled) return;

        const description = err instanceof ErrorResponse
          ? err.messages.map(m => m.description).join(", ")
          : "Failed to load machine positions.";
        enqueueSnackbar(description, { variant: "error", autoHideDuration: 4000 });
        setLoadFailed(true);
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot]);

  // Note: applies each live update to the one affected plant in place. An update older than the row
  // already held is dropped, so a late delivery cannot move a machine back to where it was.
  const handlePlantStatusUpdated = useCallback((updated: PlantPositionStatusDTO) => {
    setPlants(prev => {
      const index = prev.findIndex(p => p.plantId === updated.plantId);
      if (index === -1) return [...prev, updated];
      if (isOlder(updated, prev[index])) return prev;

      const next = [...prev];
      next[index] = updated;
      return next;
    });
  }, []);

  useEffect(() => {
    if (!connection) return;

    connection.on("PlantStatusUpdatedAsync", handlePlantStatusUpdated);

    return () => {
      connection.off("PlantStatusUpdatedAsync", handlePlantStatusUpdated);
    };
  }, [connection, handlePlantStatusUpdated]);

  // Note: SignalR does not hold messages for a client that was away, so whatever was pushed while the
  // hub was down is only recovered by reading the snapshot again once it is back.
  const isHubLive = !!connection && !isReconnecting;
  const hubWasLive = useRef<boolean | null>(null);

  useEffect(() => {
    const wasLive = hubWasLive.current;
    hubWasLive.current = isHubLive;
    if (wasLive === false && isHubLive) loadSnapshot();
  }, [isHubLive, loadSnapshot]);

  // Note: same query key GeofenceLayer uses, so this is a cache read rather than a second request.
  const { fences, isLoading: fencesLoading } = useGeofences();

  // Note: computed once the snapshot and the fences land and left alone afterwards - MapContainer only
  // reads center/zoom/bounds on its first render, so this fixes the initial view without fighting
  // live marker updates for control of the viewport on every later PlantStatusUpdatedAsync.
  //
  // Note: the FENCES are in these bounds as well as the machines, and leaving them out was a real bug
  // rather than an omission. A pit polygon is a few hundred metres across while the fences around tip
  // points are 25-90 m, so fitting to machines alone can open the map with a freshly drawn pit boundary
  // entirely outside the viewport - which reads exactly like the polygon not having saved.
  const initialBounds = useMemo<LatLngBoundsExpression | null>(() => {
    const machinePoints = plants.map(p => [p.latitude, p.longitude] as LatLngTuple);
    const points = [...machinePoints, ...fenceCorners(fences)];

    return points.length === 0 ? null : points;
  }, [plants, fences]);

  // Note: heading comes from the feed where it has one, is derived from the movement since the last
  // known position where it does not, and is otherwise held. Three sources, in that order of trust.
  // bearingBetween returns null under a few metres of travel, which is what stops a stationary
  // machine spinning on GPS jitter, and unwrapHeading keeps a machine crossing north turning the
  // short way instead of unwinding almost a full revolution.
  const positioned = useMemo(() => plants.map((plant) => {
    const previous = headingsRef.current.get(plant.plantId);

    const reported = plant.headingDegrees
      ?? (previous
        ? bearingBetween(
          { lat: previous.latitude, lng: previous.longitude },
          { lat: plant.latitude, lng: plant.longitude })
        : null);

    const heading = reported == null
      ? previous?.heading ?? 0
      : unwrapHeading(previous?.heading ?? null, reported);

    // Note: "known" is carried in the ref rather than inferred from the presence of a previous entry.
    // The entry is written on every pass INCLUDING when the heading was defaulted to north, so
    // `previous != null` only ever meant "seen before" - and because this memo re-runs on every live
    // update for any plant, an unknown heading flipped to "known" on the very next tick and started
    // asserting due north. Both excavators report a NULL course on 100% of their pings, so they were
    // the ones claiming it.
    const headingKnown = reported != null || (previous?.known ?? false);

    headingsRef.current.set(plant.plantId, {
      heading,
      known: headingKnown,
      latitude: plant.latitude,
      longitude: plant.longitude,
    });

    return { plant, heading, headingKnown };
  }), [plants]);

  const { features: mapFeatures, isSettled: mapFeaturesSettled } = useMapFeatures();
  const [storedBasemap, setStoredBasemap] = useState<Basemap | null>(readStoredBasemap);
  const basemap = sitePlanOption ? resolveBasemap(storedBasemap, mapFeaturesSettled, mapFeatures.length) : "site";

  const chooseBasemap = (sitePlan: boolean) => {
    const next: Basemap = sitePlan ? "site" : "osm";
    storeBasemap(next);
    setStoredBasemap(next);
  };

  // Note: a clock, not a subscription. Every other piece of state here arrives from SignalR, but going
  // offline is the absence of a message, so there is nothing to subscribe to - the passage of time is
  // the event.
  // Note: on by default. The fences are the frame the fleet is read against, so hiding them by default
  // would leave the map answering "where is it" but not "where is it relative to anything".
  // Note: on by default, like the fences. The roads are the map's own ground rather than an overlay -
  // without them the fences float unconnected, which is the state this was added to fix.
  const [showHaulRoads, setShowHaulRoads] = useState(true);
  const [showGeofences, setShowGeofences] = useState(true);
  const [showBlastPatterns, setShowBlastPatterns] = useState(true);
  const [showDigBlocks, setShowDigBlocks] = useState(true);

  const [offlineCheckedAt, setOfflineCheckedAt] = useState(() => DateTime.utc());
  const [mapZoom, setMapZoom] = useState<number | null>(null);
  const iconSize = markerSize(mapZoom);

  // Note: one global setting, so any plant's copy of it answers for the key. Zero until the first
  // snapshot lands.
  const offlineAfterSeconds = plants[0]?.offlineAfterSeconds ?? 0;

  useEffect(() => {
    const timer = setInterval(() => setOfflineCheckedAt(DateTime.utc()), OFFLINE_RECHECK_MS);
    return () => clearInterval(timer);
  }, []);

  // Note: colour and staleness resolved in one place so the markers, the popups and the key can never
  // disagree about what a machine is doing.
  // Note: the offline test compares a vendor-supplied timestamp against this browser's clock, so it
  // inherits any skew between the two. That is tolerable for greying out a stale marker, and the
  // alternative - trusting a server flag - cannot work at all here, because a machine that has gone
  // quiet generates no update to carry the flag. Worth revisiting if a real vendor's clock drifts far
  // enough to grey out a working fleet.
  const rendered = useMemo(() => positioned.map(({ plant, heading, headingKnown }) => {
    const silentSeconds = offlineCheckedAt.diff(plant.timestampUtc, "seconds").seconds;
    const isOffline = plant.offlineAfterSeconds > 0 && silentSeconds > plant.offlineAfterSeconds;

    // Note: an offline machine keeps the colour of whatever state it was last in rather than turning a
    // neutral grey. Eclipse's own record of it has not changed - an open stoppage is still open - so
    // "on maintenance down, and we have not heard from it since" is the true statement. The fade is
    // what says the second half of that.
    const statusColour = plant.isStopped
      ? plant.stoppageColourHex ?? STATUS_COLOURS.unclassified
      : isOffline
        ? STATUS_COLOURS.offline
        : STATUS_COLOURS.working;

    // Note: the machine's own record contradicts what it is doing. Not a display bug and not filtered
    // out: an open stoppage on a machine that is driving means either nobody closed it or it went back
    // to work without being marked UP, and catching that is a large part of why this feed exists. Shown
    // as an exception rather than quietly presenting "Maintenance Down" beside a road speed as though
    // the two agreed.
    const isMovingWhileDown = plant.isStopped
      && !isOffline
      && plant.speedKph != null
      && plant.speedKph > MOVING_WHILE_DOWN_KPH;

    return {
      plant,
      heading,
      headingKnown,
      isOffline,
      statusColour,
      silentSeconds,
      isMovingWhileDown,
      // Note: a stale heading is not a claim about travel, so the wedge goes with the fade.
      showWedge: !plant.isStopped && headingKnown && !isOffline,
    };
  }), [positioned, offlineCheckedAt]);

  const [hiddenStatuses, setHiddenStatuses] = useState<ReadonlySet<string>>(() => new Set());

  const toggleStatus = (key: string) => {
    setHiddenStatuses(prev => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  };

  // Note: an offline machine that is also down answers to both chips, so hiding either hides it.
  const shown = useMemo(
    () => rendered.filter(({ plant, isOffline }) => {
      if (isOffline && hiddenStatuses.has(OFFLINE_STATUS)) return false;
      if (plant.isStopped) return plant.stoppageTypeId == null || !hiddenStatuses.has(stoppageStatus(plant.stoppageTypeId));
      return isOffline || !hiddenStatuses.has(WORKING_STATUS);
    }),
    [rendered, hiddenStatuses]);

  // Note: the whole key, always, rather than only the states currently on screen. A key that appears
  // and disappears with the fleet is unreadable as a key - you cannot learn what a colour means from it,
  // and the absence of an entry looks like the absence of a colour rather than the absence of a machine.
  // Note: the stoppage types and their colours come from the same endpoint the Stoppages screen uses, so
  // the key cannot drift from the database. Up first, then the types in their own id order, then Offline
  // last - it is a device state rather than a work state, so it does not belong among them.
  const legend = useMemo(() => {
    const entries: { key: string; code: string; label: string; colour: string; faded: boolean }[] = [
      { key: WORKING_STATUS, code: "Up", label: "Working", colour: STATUS_COLOURS.working, faded: false },
    ];

    for (const type of stoppageTypes) {
      entries.push({
        key: stoppageStatus(type.stoppageTypeId),
        code: abbreviateStoppageType(type.stoppageTypeId, type.stoppageType),
        label: type.stoppageType,
        colour: type.colourHex ?? STATUS_COLOURS.unclassified,
        faded: false,
      });
    }

    entries.push({
      key: OFFLINE_STATUS,
      code: "Off",
      // Note: the threshold is a server setting, so the label reports whatever it actually is rather
      // than a number written in here that could drift from it. Omitted entirely until a machine has
      // told us what it is, instead of restating the server's default and risking being wrong.
      label: offlineAfterSeconds > 0
        ? `Offline - nothing heard for over ${formatSilence(offlineAfterSeconds)}, shown at its last known position`
        : "Offline - shown at its last known position",
      colour: STATUS_COLOURS.offline,
      faded: true,
    });

    return entries;
  }, [stoppageTypes, offlineAfterSeconds]);

  const emptyMessage = loadFailed
    ? "Machine positions could not be loaded."
    : "No plant has reported a position yet.";

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 shrink-0">
        {title}

        {/* Note: the key renders the same markup the map does, via equipmentMarkerHtml, so the
            swatches cannot drift out of step with the markers if the palette or wedge changes.
            A fixed colour dot was already wrong once the silhouettes landed - and for the same
            reason the key has to follow the map back to dots when the sprite is unavailable,
            rather than describing symbols that are not on screen. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-theme-text-muted">
          {sitePlanOption && (
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={basemap === "site"}
                onChange={e => chooseBasemap(e.target.checked)}
                className="w-4 h-4 border-theme-border rounded-sm"
              />
              Site plan
            </label>
          )}
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showHaulRoads}
              onChange={e => setShowHaulRoads(e.target.checked)}
              className="w-4 h-4 border-theme-border rounded-sm"
            />
            Haul roads
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showGeofences}
              onChange={e => setShowGeofences(e.target.checked)}
              className="w-4 h-4 border-theme-border rounded-sm"
            />
            Geofences
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showBlastPatterns}
              onChange={e => setShowBlastPatterns(e.target.checked)}
              className="w-4 h-4 border-theme-border rounded-sm"
            />
            Blast patterns
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showDigBlocks}
              onChange={e => setShowDigBlocks(e.target.checked)}
              className="w-4 h-4 border-theme-border rounded-sm"
            />
            Dig blocks
          </label>
          {legend.map(entry => {
            const isShown = !hiddenStatuses.has(entry.key);

            return (
              <Tooltip key={entry.key} title={entry.label} arrow placement="top">
                <Chip
                  label={entry.code}
                  size="small"
                  aria-label={entry.label}
                  aria-pressed={isShown}
                  onClick={() => toggleStatus(entry.key)}
                  className={isShown
                    ? "!bg-theme-primary !text-theme-primary-contrast !border !border-theme-primary"
                    : "!bg-theme-surface !text-theme-text-muted !border !border-theme-border"}
                  icon={(
                    // Note: the dot alone, not a silhouette wearing it. The key explains the status
                    // colours, and the marker's shape is a separate thing the key has no view on -
                    // putting a haul truck beside every colour implied the two were related.
                    <span
                      className="inline-block w-3 h-3 rounded-full border border-white"
                      style={{
                        backgroundColor: entry.colour,
                        opacity: entry.faded || !isShown ? OFFLINE_OPACITY : 1,
                        boxShadow: "0 0 0 1px rgba(0,0,0,.45)",
                      }}
                    />
                  )}
                />
              </Tooltip>
            );
          })}
        </div>
      </div>

      {/* Note: `isolate` is load-bearing, not cosmetic. Leaflet gives its zoom control z-index 1000 and
          its panes 400, and a positioned element with z-index:auto - which the nav drawer and the modal
          wrapper both are - always loses to a positive z-index no matter how late it sits in the DOM.
          Without a stacking context here the +/- buttons paint straight through the open menu and
          through any modal opened over this page. Isolating the map confines those numbers inside it. */}
      <div className="isolate flex-1 min-h-0 rounded-lg border border-theme-border overflow-hidden relative">
        {(isLoading || fencesLoading) && (
          <div className="absolute inset-0 flex items-center justify-center bg-theme-surface z-[1000]">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-theme-text-muted" />
          </div>
        )}

        {!isLoading && !fencesLoading && !initialBounds && (
          <DashboardCardEmptyState
            icon={faLocationDot}
            title="No machine positions"
            subtitle={emptyMessage}
          />
        )}

        {!isLoading && !fencesLoading && initialBounds && plants.length === 0 && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] rounded-sm border border-theme-border bg-theme-surface px-3 py-1 text-sm text-theme-text-muted">
            {emptyMessage}
          </div>
        )}

        {!isLoading && !fencesLoading && initialBounds && (
          <MapContainer preferCanvas maxZoom={MAP_MAX_ZOOM} zoomSnap={MAP_ZOOM_SNAP} bounds={initialBounds} boundsOptions={{ padding: [40, 40] }} style={{ height: "100%", width: "100%" }}>
            {basemap === "osm" && (
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={MAP_MAX_ZOOM}
                maxNativeZoom={OSM_MAX_NATIVE_ZOOM}
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
            )}
            <MapZoomReporter onZoom={setMapZoom} />
            <MapResizeWatcher />
            <LayerStack>
              <MapFeatureLayer sitePlan={basemap === "site"} />
              <GeofenceLayer visible={showGeofences} />
              <HaulRoadLayer visible={showHaulRoads} />
              <BlastPatternFenceLayer visible={showBlastPatterns} />
              <DigBlockFenceLayer visible={showDigBlocks} />

              <StackedGroup rank={LAYER_RANK.machines}>
                {shown.map(({ plant, heading, headingKnown, isOffline, statusColour, showWedge, silentSeconds, isMovingWhileDown }) => {
                  const popup = (
                    <Popup>
                      <div className="text-sm text-theme-text">
                        <div className="font-semibold">{plant.plantRef}</div>
                        {isEquipmentType(plant.equipmentType) && (
                          <div className="text-theme-text-muted">{equipmentLabel(plant.equipmentType)}</div>
                        )}
                        <div>
                          {plant.isStopped ? plant.stoppageType ?? "Stopped" : "Moving"}
                          {plant.speedKph != null ? ` - ${plant.speedKph.toFixed(1)} km/h` : ""}
                          {showWedge ? ` - heading ${Math.round(((heading % 360) + 360) % 360).toString().padStart(3, "0")}` : ""}
                        </div>
                        <div>{plant.locationName ?? "Unknown location"}</div>
                        {/* Note: an offline machine says so in words as well as by fading, and says how
                            long it has been quiet - the fade alone cannot distinguish "just missed a
                            couple of pings" from "gone since yesterday". */}
                        {isOffline && (
                          <div className="font-semibold">
                            Offline - no report for {formatSilence(silentSeconds)}
                          </div>
                        )}
                        {isMovingWhileDown && (
                          <div className="font-semibold text-theme-warning">
                            Moving while marked down - the stoppage may need closing
                          </div>
                        )}
                        <div className="text-xs text-theme-text-muted">{plant.timestampUtc.toFormat("dd LLL HH:mm:ss")}</div>
                      </div>
                    </Popup>
                  );

                  // Note: a machine class with no silhouette keeps the original dot rather than borrowing
                  // another machine's shape, so the map never states something it does not know. The
                  // same dot carries the whole fleet when the sprite is unavailable, including while it
                  // is still in flight - drawing a silhouette before its symbol exists would rely on
                  // `use` re-resolving a fragment that appears after render, which is not worth trusting
                  // when the fallback costs nothing.
                  if (spriteStatus !== "ready" || !isEquipmentType(plant.equipmentType)) {
                    return (
                      <CircleMarker
                        key={plant.plantId}
                        center={[plant.latitude, plant.longitude]}
                        radius={8}
                        pathOptions={{
                          weight: 2,
                          color: statusColour,
                          fillColor: statusColour,
                          fillOpacity: isOffline ? OFFLINE_OPACITY : 0.85,
                          opacity: isOffline ? OFFLINE_OPACITY : 1,
                        }}
                      >
                        {popup}
                      </CircleMarker>
                    );
                  }

                  // Note: anchored at the centre of the icon, not its base. The silhouette rotates about
                  // its own middle, so a base anchor would swing the machine off its own coordinate as
                  // it turned.
                  return (
                    <Marker
                      key={plant.plantId}
                      position={[plant.latitude, plant.longitude]}
                      zIndexOffset={isOffline ? OFFLINE_Z_OFFSET : 0}
                      icon={divIcon({
                        className: "eq-marker",
                        html: equipmentMarkerHtml({
                          type: plant.equipmentType,
                          heading,
                          size: iconSize,
                          moving: showWedge,
                          statusColour,
                          opacity: isOffline ? OFFLINE_OPACITY : 1,
                        }),
                        iconSize: [iconSize, iconSize],
                        iconAnchor: [iconSize / 2, iconSize / 2],
                      })}
                    >
                      {popup}
                    </Marker>
                  );
                })}
              </StackedGroup>
            </LayerStack>
          </MapContainer>
        )}
      </div>
    </>
  );
};

